import { ENV } from '@/lib/config/env';

interface CompetitorLike {
  name?: string;
  website?: string;
}

// Anchored to the end and stripped one at a time, so "AG Jeans" keeps its "AG" while
// "The Estee Lauder Companies Inc." reduces to the brand. A descriptor the brand is known by
// ("Beauty", "Cosmetics", "Paris") is deliberately not in here.
const TRAILING_LEGAL_SUFFIX =
  /[\s,]+(?:incorporated|inc|corporation|corp|companies|company|holdings|group|limited|ltd|llc|plc|gmbh|nv|ag|sa|co)\.?$/;

function stripLegalSuffixes(name: string): string {
  let stripped = name;
  for (;;) {
    const next = stripped.replace(TRAILING_LEGAL_SUFFIX, '');
    if (next === stripped) return stripped;
    stripped = next;
  }
}

/**
 * Lowercase alphanumerics only, so "e.l.f. Cosmetics" and "elf cosmetics" compare equal.
 * Legal suffixes go too: the same brand arrives as "Estee Lauder Inc." from one source and
 * "The Estee Lauder Companies Inc." from another, and those must compare equal.
 */
export function normaliseBrandName(name: string): string {
  // Decompose first so an accent folds to its base letter. Dropping it outright would spell
  // "Estee Lauder" as "estelauder" and guess a domain that is one letter short of the real one.
  const folded = name
    .replace(/\s*\(.*/, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return stripLegalSuffixes(folded)
    .replace(/^the\s+/, '')
    .replace(/[^a-z0-9]/g, '');
}

function toHostname(website: string): string | null {
  try {
    const url = website.startsWith('http') ? website : `https://${website}`;
    const hostname = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
    return hostname || null;
  } catch {
    return null;
  }
}

/** Normalised competitor name to bare hostname, from the domains the analysis already holds. */
export function competitorDomains(competitors: CompetitorLike[] | undefined): Map<string, string> {
  const domains = new Map<string, string>();
  for (const competitor of competitors ?? []) {
    if (!competitor.name || !competitor.website) continue;
    const hostname = toHostname(competitor.website);
    const key = normaliseBrandName(competitor.name);
    if (hostname && key) domains.set(key, hostname);
  }
  return domains;
}

/**
 * Tiered on purpose: a competitor match carries a real domain, and the squashed `.com` guess
 * is the fallback. The guess is worth making for any name because the icon proxy validates
 * what it fetches and 404s a miss, which shows the colour swatch. The residual risk is a
 * guess that resolves to a real but unrelated company, whose logo then renders.
 */
export function resolveAdvertiserDomain(
  advertiser: string,
  domains: Map<string, string>,
): string | null {
  const key = normaliseBrandName(advertiser);
  if (!key) return null;

  const exact = domains.get(key);
  if (exact) return exact;

  for (const [competitorKey, hostname] of domains) {
    if (key.startsWith(competitorKey) || competitorKey.startsWith(key)) return hostname;
  }

  return `${key}.com`;
}

/** Backend proxy that validates the icon and 404s on a miss, so the fallback shows. */
export function brandIconUrl(domain: string): string {
  const baseUrl = ENV.INFERENCE_ENGINE_API_BASE_URL.trim().replace(/\/+$/, '');
  return `${baseUrl}/api/v1/brand-icon?domain=${encodeURIComponent(domain)}`;
}
